import { useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetFavoriteCollection,
  useUpdateFavoriteCollection,
  useDeleteFavoriteCollection,
  getListFavoriteCollectionsQueryKey,
  getGetFavoriteCollectionQueryKey,
} from "@workspace/api-client-react";
import type { RecipeRecord } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { RecipeCard } from "@/components/RecipeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Folder, Pencil, Trash2, ArrowLeft, Check, X } from "lucide-react";

export default function CollectionDetailPage() {
  const params = useParams<{ id: string }>();
  const collectionId = Number.parseInt(params.id, 10);
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, error } = useGetFavoriteCollection(collectionId, {
    query: { enabled: Number.isFinite(collectionId) },
  });

  const renameMut = useUpdateFavoriteCollection({
    mutation: {
      onSuccess: () => {
        toast({ title: "Collection renamed" });
        setEditing(false);
        void qc.invalidateQueries({
          queryKey: getListFavoriteCollectionsQueryKey(),
        });
        void qc.invalidateQueries({
          queryKey: getGetFavoriteCollectionQueryKey(collectionId),
        });
      },
      onError: () =>
        toast({ title: "Rename failed", variant: "destructive" }),
    },
  });

  const deleteMut = useDeleteFavoriteCollection({
    mutation: {
      onSuccess: () => {
        toast({
          title: "Collection deleted",
          description: "The recipes themselves are still in your cookbook.",
        });
        void qc.invalidateQueries({
          queryKey: getListFavoriteCollectionsQueryKey(),
        });
        navigate("/favorites");
      },
      onError: () =>
        toast({ title: "Delete failed", variant: "destructive" }),
    },
  });

  const collection = data?.collection;
  const recipes = (data?.recipes ?? []) as RecipeRecord[];

  const startEdit = () => {
    setDraftName(collection?.name ?? "");
    setEditing(true);
  };

  const submitRename = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draftName.trim();
    if (!name || name === collection?.name) {
      setEditing(false);
      return;
    }
    renameMut.mutate({ collectionId, data: { name } });
  };

  if (!Number.isFinite(collectionId)) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Invalid collection.</p>
          <Link
            href="/favorites"
            className="inline-flex items-center gap-1 mt-4 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to favorites
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <Link
          href="/favorites"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to favorites
        </Link>

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20">
              <Folder className="w-6 h-6 text-primary" />
            </div>
            <div className="min-w-0">
              {editing ? (
                <form onSubmit={submitRename} className="flex items-center gap-2">
                  <Input
                    autoFocus
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    maxLength={100}
                    className="h-9 text-lg font-bold w-64"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8"
                    disabled={renameMut.isPending}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    onClick={() => setEditing(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </form>
              ) : (
                <>
                  <h1
                    className="text-3xl font-bold text-foreground truncate"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {collection?.name ?? "Collection"}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isLoading
                      ? "Loading…"
                      : recipes.length === 0
                        ? "No recipes yet — open any recipe and tap “Collections” to add it here."
                        : `${recipes.length} recipe${recipes.length === 1 ? "" : "s"}`}
                  </p>
                </>
              )}
            </div>
          </div>

          {!editing && collection && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={startEdit}
              >
                <Pencil className="w-3.5 h-3.5" /> Rename
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </Button>
            </div>
          )}
        </header>

        {error ? (
          <div className="py-16 text-center text-muted-foreground">
            Could not load this collection.
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/5] rounded-2xl bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : recipes.length === 0 ? (
          <div className="py-16 text-center">
            <Link
              href="/"
              className="inline-block px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Browse the cookbook
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {recipes.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this collection?</AlertDialogTitle>
            <AlertDialogDescription>
              The collection “{collection?.name}” will be removed. The recipes
              inside it stay in your cookbook and stay favorited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMut.mutate({ collectionId })}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
